import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { getDirectors, getVideos, createVideo, updateVideo, deleteVideo } from './api';

export default function VideosPanel() {
  const { token } = useOutletContext();
  const [directors, setDirectors] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ director_id: '', vimeo_id: '', title: '', description: '', thumbnail: '', client: '', active: true });
  const [editingId, setEditingId] = useState(null);
  const [selectedDirector, setSelectedDirector] = useState('');

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const [dirs, vids] = await Promise.all([
        getDirectors(token),
        getVideos(token)
      ]);
      setDirectors(dirs);
      setVideos(vids);
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token]);
// Add a reload prop if needed for external reloads
// (This will be used in the admin dashboard if we want to force reload after director changes)


  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    console.log('Submitting form:', form); // Debug log
    try {
      let response;
      if (editingId) {
        console.log('Updating video with ID:', editingId);
        response = await updateVideo(editingId, form, token);
        console.log('Update response:', response);
      } else {
        console.log('Creating new video');
        response = await createVideo(form, token);
        console.log('Create response:', response);
      }
      setForm({ director_id: '', vimeo_id: '', title: '', description: '', thumbnail: '', client: '', active: true });
      setEditingId(null);
      await fetchData();
    } catch (err) {
      console.error('Error saving video:', err);
      setError(`Failed to save video: ${err.message}`);
    }
  };

  const handleEdit = v => {
    setEditingId(v.id);
    setForm({
      director_id: v.director_id || '',
      vimeo_id: v.vimeo_id || '',
      title: v.title || '',
      description: v.description || '',
      thumbnail: v.thumbnail || '',
      client: v.client || '',
      active: !!v.active
    });
  };

  const handleDelete = async id => {
    if (!window.confirm('Delete this video?')) return;
    try {
      await deleteVideo(id, token);
      fetchData();
    } catch (err) {
      setError('Failed to delete video');
    }
  };

  const filteredVideos = selectedDirector
    ? videos.filter(v => v.director_id === Number(selectedDirector))
    : videos;

  return (
    <div style={{ margin: '32px 0' }}>
      <h3>Videos</h3>
      {loading ? <div>Loading...</div> : null}
      {error && <div style={{ color: 'red' }}>{error}</div>}
      <div style={{ marginBottom: 12 }}>
        <div className="mb-4">
          <label className="text-sm text-text-secondary mr-2">Filter by Director:</label>
          <select 
            value={selectedDirector} 
            onChange={e => setSelectedDirector(e.target.value)}
            className="bg-bg-tertiary border border-border text-text-primary rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent"
          >
            <option value="">All Directors</option>
            {directors.map(d => (
              <option key={d.id} value={d.id}>{d.first_name} {d.last_name}</option>
            ))}
          </select>
        </div>
      </div>
      <form onSubmit={handleSubmit} style={{ marginBottom: 24 }}>
        <select 
          name="director_id" 
          value={form.director_id} 
          onChange={handleChange} 
          required
          className="bg-bg-tertiary border border-border text-text-primary rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent mr-2"
        >
          <option value="">Select Director</option>
          {directors.map(d => (
            <option key={d.id} value={d.id}>
              {d.first_name} {d.last_name}
            </option>
          ))}
        </select>
        <input name="vimeo_id" value={form.vimeo_id} onChange={handleChange} placeholder="Vimeo ID" required style={{ marginRight: 8 }} />
        <input name="title" value={form.title} onChange={handleChange} placeholder="Title" style={{ marginRight: 8 }} />
        <input name="client" value={form.client} onChange={handleChange} placeholder="Client" style={{ marginRight: 8 }} />
        <label style={{ marginRight: 8 }}>
          <input type="checkbox" name="active" checked={form.active} onChange={handleChange} /> Active
        </label>
        <br />
        <textarea name="description" value={form.description} onChange={handleChange} placeholder="Description" rows={3} style={{ width: 350, marginTop: 8, marginRight: 8 }} />
        <input type="file" accept="image/jpeg,image/jpg" onChange={e => {
  const file = e.target.files[0];
  if (file && file.type.match(/^image\/(jpeg|jpg)$/)) {
    const reader = new FileReader();
    reader.onload = ev => setForm(f => ({ ...f, thumbnail: ev.target.result }));
    reader.readAsDataURL(file);
  } else {
    setForm(f => ({ ...f, thumbnail: '' }));
  }
}} style={{ marginRight: 8 }} />
{form.thumbnail && <img src={form.thumbnail} alt="thumb preview" style={{ width: 40, verticalAlign: 'middle', marginLeft: 8 }} />}
        <br />
        <button type="submit" style={{ marginTop: 8 }}>{editingId ? 'Update' : 'Add'} Video</button>
        {editingId && <button type="button" onClick={() => { setEditingId(null); setForm({ director_id: '', vimeo_id: '', title: '', description: '', thumbnail: '', client: '', active: true }); }} style={{ marginLeft: 8 }}>Cancel</button>}
      </form>
      <table border="1" cellPadding="6" style={{ borderCollapse: 'collapse', width: '100%' }}>
        <thead>
          <tr>
            <th>Director</th>
            <th>Vimeo ID</th>
            <th>Title</th>
            <th>Client</th>
            <th>Active</th>
            <th>Thumbnail</th>
            <th>Description</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredVideos.map(v => (
            <tr key={v.id}>
              <td>{v.director_first_name} {v.director_last_name || 'None'}</td>
              <td>{v.vimeo_id}</td>
              <td>{v.title}</td>
              <td>{v.client || '-'}</td>
              <td>{v.active ? 'Yes' : 'No'}</td>
              <td>{v.thumbnail ? <img src={v.thumbnail} alt="thumb" style={{ width: 40 }} /> : ''}</td>
              <td style={{ maxWidth: 240, whiteSpace: 'pre-wrap' }}>{v.description}</td>
              <td>
                <button onClick={() => handleEdit(v)}>Edit</button>
                <button onClick={() => handleDelete(v.id)} style={{ marginLeft: 8 }}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
